import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { apiFetch } from '../../../api/client';
import { PageMeta } from '../shared/PageMeta';

const applySchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email'),
  role: z.enum(['donor', 'recipient'], { message: 'Please select a role' }),
  organizationName: z.string().min(1, 'Organization name is required'),
  organizationType: z.string().min(1, 'Organization type is required'),
  country: z.string().min(2, 'Country is required'),
  message: z.string().optional(),
});

type ApplyForm = z.infer<typeof applySchema>;

export function ApplyPage() {
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get('role') as 'donor' | 'recipient' | null;
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<ApplyForm>({
    resolver: zodResolver(applySchema),
    defaultValues: {
      role: preselectedRole === 'donor' || preselectedRole === 'recipient' ? preselectedRole : undefined,
    },
  });

  const onSubmit = async (data: ApplyForm) => {
    setLoading(true);
    try {
      await apiFetch('/api/account-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      toast.success('Application submitted!');
      setSubmitted(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-16">
        <PageMeta title="Application Submitted" description="Your account application has been submitted" path="/marketplace/apply" />
        <div className="max-w-md mx-auto text-center">
          <div className="card p-8">
            <div className="w-16 h-16 rounded-full bg-spi-green/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-spi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl mb-2">Application Submitted</h2>
            <p className="text-spi-slate text-sm">
              Thank you for your interest! We'll review your application and be in touch within a few business days.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <PageMeta title="Apply for an Account" description="Apply to join the Second Chance Seed Network" path="/marketplace/apply" />
      <div className="max-w-lg mx-auto">
        <div className="card p-8">
          <h2 className="text-2xl mb-2 text-center">Apply for an Account</h2>
          <p className="text-spi-slate text-sm text-center mb-8">
            Tell us about yourself and your organization. Our team will review your application and create your account.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="input-label">Full Name</label>
              <input {...register('name')} type="text" className="input" placeholder="John Smith" />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
            </div>

            <div>
              <label className="input-label">Email</label>
              <input {...register('email')} type="email" className="input" placeholder="you@example.com" />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="input-label">I want to join as a...</label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input {...register('role')} type="radio" value="donor" className="accent-spi-green" />
                  <span className="text-sm">Donor</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input {...register('role')} type="radio" value="recipient" className="accent-spi-green" />
                  <span className="text-sm">Recipient</span>
                </label>
              </div>
              {errors.role && <p className="text-red-500 text-xs mt-1">{errors.role.message}</p>}
            </div>

            <div>
              <label className="input-label">Organization Name</label>
              <input {...register('organizationName')} type="text" className="input" placeholder="Acme Seeds Inc." />
              {errors.organizationName && <p className="text-red-500 text-xs mt-1">{errors.organizationName.message}</p>}
            </div>

            <div>
              <label className="input-label">Organization Type</label>
              <select {...register('organizationType')} className="input">
                <option value="">Select type...</option>
                <option value="company">Company</option>
                <option value="foundation">Foundation</option>
                <option value="nonprofit">Non-Profit</option>
                <option value="school">School</option>
                <option value="government">Government</option>
                <option value="other">Other</option>
              </select>
              {errors.organizationType && <p className="text-red-500 text-xs mt-1">{errors.organizationType.message}</p>}
            </div>

            <div>
              <label className="input-label">Country</label>
              <input {...register('country')} type="text" className="input" placeholder="Uganda" />
              {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country.message}</p>}
            </div>

            <div>
              <label className="input-label">Message <span className="text-spi-slate font-normal">(optional)</span></label>
              <textarea
                {...register('message')}
                className="input min-h-[100px]"
                placeholder="Tell us about your organization and why you'd like to join..."
              />
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary w-full">
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
